import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Zap, Building2, Camera, Users, Shield, Clock, ArrowRight, ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CheckoutTransparente from '@/components/CheckoutTransparente';
import { API_BASE_URL } from '@/lib/authFetch';

interface Plano {
  id: number;
  nome: string;
  slug: string;
  valor: number;
  descricao: string;
  max_cameras: number;
  max_usuarios: number;
  recursos: string[];
  destaque?: boolean;
  dias_trial: number;
}

const Planos = () => {
  const navigate = useNavigate();
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState<Plano | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsLoggedIn(!!token);
    loadPlanos();
  }, []);

  const loadPlanos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/planos`);
      const data = await response.json();
      setPlanos(data.planos || []);
    } catch {
      // Erro ao carregar planos
    } finally {
      setLoading(false);
    }
  };

  const handleAssinar = (plano: Plano) => {
    if (!isLoggedIn) {
      toast.error('Faça login para assinar um plano');
      navigate('/?redirect=/planos');
      return;
    }

    // Abre o checkout transparente (modal)
    setSelectedPlano({
      ...plano,
      id: plano.slug as any, // Usar slug como ID para o checkout
      descricao: plano.descricao || plano.nome
    });
    setShowCheckout(true);
  };

  const handleCheckoutSuccess = (data: any) => {
    toast.success('Assinatura ativada com sucesso!');
    setShowCheckout(false);
    setSelectedPlano(null);
    
    // Redireciona para o dashboard
    setTimeout(() => {
      navigate('/dashboard');
    }, 1000);
  };

  const handleCheckoutCancel = () => {
    setShowCheckout(false);
    setSelectedPlano(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-protec-dark">
      {/* Header Simples */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-protec-dark/95 backdrop-blur-sm border-b border-protec-gold/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            className="text-gray-400 hover:text-white"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </Button>
          <h1 className="text-xl font-bold text-protec-gold">ProtecBens</h1>
          {isLoggedIn ? (
            <Button
              variant="outline"
              className="border-protec-gold text-protec-gold"
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </Button>
          ) : (
            <Button
              variant="outline"
              className="border-protec-gold text-protec-gold"
              onClick={() => navigate('/')}
            >
              Login
            </Button>
          )}
        </div>
      </header>
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <Badge className="bg-protec-gold/20 text-protec-gold mb-4">
              PLANOS E PREÇOS
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Escolha o plano ideal para você
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Proteção inteligente com tecnologia de ponta. 
              Cancele quando quiser, sem multas.
            </p>
          </div>

          {/* Cards de Planos */}
          {loading ? (
            <div className="text-center py-16">
              <RefreshCw className="w-12 h-12 text-protec-gold animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Carregando planos...</p>
            </div>
          ) : planos.length === 0 ? (
            <div className="text-center py-16 max-w-md mx-auto">
              <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Nenhum plano disponível</h3>
              <p className="text-gray-400 mb-4">
                Os planos ainda não foram configurados. Entre em contato com o administrador.
              </p>
              <Button variant="outline" className="border-protec-gold text-protec-gold" onClick={() => navigate('/')}>
                Voltar
              </Button>
            </div>
          ) : (
            <div className={`grid gap-8 max-w-6xl mx-auto ${
              planos.length === 1 ? 'md:grid-cols-1 max-w-md' : 
              planos.length === 2 ? 'md:grid-cols-2 max-w-3xl' : 
              'md:grid-cols-3'
            }`}>
              {planos.map((plano, index) => (
                <Card 
                  key={plano.id}
                  className={`relative bg-protec-dark-light border-2 transition-all duration-300 hover:scale-105 ${
                    plano.destaque 
                      ? 'border-protec-gold shadow-lg shadow-protec-gold/20' 
                      : 'border-gray-700 hover:border-protec-gold/50'
                  }`}
                >
                  {/* Badge Popular */}
                  {plano.destaque && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-protec-gold text-black font-bold px-4 py-1">
                        <Star className="w-4 h-4 mr-1 fill-current" />
                        MAIS POPULAR
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="text-center pt-8">
                    <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                      plano.destaque ? 'bg-protec-gold' : 'bg-gray-700'
                    }`}>
                      {index === 0 && <Camera className={`w-8 h-8 ${plano.destaque ? 'text-black' : 'text-white'}`} />}
                      {index === 1 && <Zap className={`w-8 h-8 ${plano.destaque ? 'text-black' : 'text-white'}`} />}
                      {index >= 2 && <Building2 className={`w-8 h-8 ${plano.destaque ? 'text-black' : 'text-white'}`} />}
                    </div>
                    
                    <CardTitle className="text-2xl text-white">{plano.nome}</CardTitle>
                    <CardDescription className="text-gray-400">{plano.descricao}</CardDescription>
                    
                    <div className="mt-6">
                      <span className="text-5xl font-bold text-white">
                        {formatCurrency(plano.valor).replace('R$', 'R$ ')}
                      </span>
                      <span className="text-gray-400">/mês</span>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-4">
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-protec-dark rounded-lg">
                      <div className="text-center">
                        <Camera className="w-5 h-5 text-protec-gold mx-auto mb-1" />
                        <p className="text-white font-bold">
                          {plano.max_cameras === -1 ? '∞' : plano.max_cameras}
                        </p>
                        <p className="text-xs text-gray-400">Câmeras</p>
                      </div>
                      <div className="text-center">
                        <Users className="w-5 h-5 text-protec-gold mx-auto mb-1" />
                        <p className="text-white font-bold">
                          {plano.max_usuarios === -1 ? '∞' : plano.max_usuarios}
                        </p>
                        <p className="text-xs text-gray-400">Usuários</p>
                      </div>
                    </div>

                    {/* Recursos */}
                    {plano.recursos && plano.recursos.length > 0 && (
                      <ul className="space-y-3">
                        {plano.recursos.map((recurso, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-300 text-sm">{recurso}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>

                  <CardFooter className="pt-6">
                    <Button
                      className={`w-full py-6 text-lg font-semibold ${
                        plano.destaque
                          ? 'bg-protec-gold text-black hover:bg-protec-gold-light'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                      onClick={() => handleAssinar(plano)}
                    >
                      Assinar agora
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}

          {/* Garantias */}
          <div className="mt-16 grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="flex items-center gap-4 text-center md:text-left">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <Shield className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-white font-semibold">Pagamento Seguro</p>
                <p className="text-gray-400 text-sm">Via Mercado Pago</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-center md:text-left">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Clock className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-white font-semibold">Cancele quando quiser</p>
                <p className="text-gray-400 text-sm">Sem multa ou fidelidade</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-center md:text-left">
              <div className="p-3 bg-protec-gold/20 rounded-lg">
                <Star className="w-6 h-6 text-protec-gold" />
              </div>
              <div>
                <p className="text-white font-semibold">7 dias grátis</p>
                <p className="text-gray-400 text-sm">Teste sem compromisso</p>
              </div>
            </div>
          </div>

          {/* FAQ Simples */}
          <div className="mt-20 text-center">
            <h2 className="text-2xl font-bold text-white mb-8">Dúvidas frequentes</h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
              <div className="bg-protec-dark-light p-6 rounded-lg">
                <h3 className="text-white font-semibold mb-2">Como funciona o período de teste?</h3>
                <p className="text-gray-400 text-sm">
                  Você tem 7 dias para testar todas as funcionalidades do plano escolhido. 
                  Se não gostar, cancele antes do término e não será cobrado.
                </p>
              </div>
              <div className="bg-protec-dark-light p-6 rounded-lg">
                <h3 className="text-white font-semibold mb-2">Posso trocar de plano depois?</h3>
                <p className="text-gray-400 text-sm">
                  Sim! Você pode fazer upgrade ou downgrade a qualquer momento. 
                  O valor será ajustado proporcionalmente.
                </p>
              </div>
              <div className="bg-protec-dark-light p-6 rounded-lg">
                <h3 className="text-white font-semibold mb-2">Quais formas de pagamento?</h3>
                <p className="text-gray-400 text-sm">
                  Aceitamos cartão de crédito através do Mercado Pago com total segurança.
                </p>
              </div>
              <div className="bg-protec-dark-light p-6 rounded-lg">
                <h3 className="text-white font-semibold mb-2">Preciso de equipamento especial?</h3>
                <p className="text-gray-400 text-sm">
                  Não! Nosso sistema é 100% na nuvem. Você só precisa de câmeras IP compatíveis e internet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Simples */}
      <footer className="bg-protec-dark-light border-t border-protec-gold/20 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            © 2024 ProtecBens - Vigilância Nacional. Todos os direitos reservados.
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Pagamentos processados com segurança pelo Mercado Pago
          </p>
        </div>
      </footer>

      {/* Modal de Checkout Transparente */}
      {showCheckout && selectedPlano && (
        <CheckoutTransparente
          plano={selectedPlano}
          onSuccess={handleCheckoutSuccess}
          onCancel={handleCheckoutCancel}
        />
      )}
    </div>
  );
};

export default Planos;
